// File: src/pages/DashboardPage.js

import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from 'axios';
import toast from 'react-hot-toast';
import { jwtDecode } from "jwt-decode";

import {
  FaSearch, FaPills, FaTrash, FaCashRegister, FaCreditCard,
  FaReceipt, FaPlus, FaMinus, FaImage
} from "react-icons/fa";

import './DashboardPage.css';
import SideNav from '../components/SideNav';
import useLicenseCheck from "../hooks/useLicenseCheck";

const IMAGE_BASE_URL = "https://installmentzone.com/pharmacy/";
const POLLING_INTERVAL = 20000;

const formatCurrency = (amount) => {
  const numericAmount = Number(amount) || 0;
  return `GH₵ ${numericAmount.toFixed(2)}`;
};

const ProductCard = ({ product, onAddToCart }) => (
  <div className="product-card" onClick={() => onAddToCart(product)}>
    <div className="product-card-image-wrapper">
      {product.image_url ? (
        <img
          src={`${IMAGE_BASE_URL}${product.image_url}`}
          alt={product.name}
          className="product-card-image"
        />
      ) : (
        <div className="product-card-image-placeholder">
          <FaImage size={40} color="#e2e8f0" />
        </div>
      )}
    </div>

    <div className="product-card-info">
      <h3>{product.name}</h3>
      <p>{product.category || 'Uncategorized'}</p>
    </div>

    <div className="product-card-details">
      <span className={`stock-level ${product.quantity < 50 ? 'low' : ''}`}>
        Stock: {product.quantity}
      </span>

      <span className="product-price">
        {formatCurrency(product.price)}
      </span>
    </div>
  </div>
);

const DashboardPage = () => {
  useLicenseCheck(); // 

  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [cart, setCart] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [paymentMethod, setPaymentMethod] = useState("Cash");
  const [taxRate, setTaxRate] = useState(5);
  const [discountAmount, setDiscountAmount] = useState(0);

  const API_BASE_URL =
    process.env.REACT_APP_API_BASE_URL ||
    "https://installmentzone.com/pharmacy/api";

  const getToken = () => localStorage.getItem("token");

  const isTokenValid = (token) => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      return decoded.exp > currentTime;
    } catch {
      return false;
    }
  };

  useEffect(() => {
  const fetchData = async (isInitial = false) => {
    const token = getToken();

    if (!token || !isTokenValid(token)) {
      toast.error("Session expired. Please login again.");
      localStorage.removeItem("token");
      navigate("/");
      return;
    }

    if (isInitial) setLoading(true);

    // --- Fetch user details ---
    try {
      const userRes = await axios.get(`${API_BASE_URL}/get_user_details.php`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { status, message } = userRes.data;

      if (status === "expired") {
        toast.error("Your license has expired. Please renew!");
        localStorage.removeItem("token");
        navigate("/renew-license");
        return;
      }

      if (status === "success") {
        setUser(userRes.data.user);
      } else {
        toast.error(message || "Failed to fetch user details");
      }

    } catch (err) {
      toast.error("Session expired. Please login again.");
      localStorage.removeItem("token");
      navigate("/");
      return;
    }

    // --- Fetch products ---
    try {
      const prodRes = await axios.get(`${API_BASE_URL}/get_products.php?limit=1000`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { status, data, message } = prodRes.data;

      if (status === "expired") {
        toast.error("Your license has expired. Please renew!");
        localStorage.removeItem("token");
        navigate("/renew-license");
        return;
      }

      if (status === "success" && Array.isArray(data)) {
        setProducts(data);
      } else {
        toast.error(message || "Failed to load products");
      }

    } catch (err) {
      toast.error("Failed to load products. Please check your connection.");
    } finally {
      if (isInitial) setLoading(false);
    }
  };

  fetchData(true);
  const intervalId = setInterval(() => fetchData(false), POLLING_INTERVAL);
  return () => clearInterval(intervalId);

}, [navigate, API_BASE_URL]);

  const productCategories = [
    "All",
    ...new Set(products.map(p => p.category || "Uncategorized"))
  ];

  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesCategory =
        activeCategory === "All" || p.category === activeCategory;

      const matchesSearch =
        p.name.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesCategory && matchesSearch;
    });
  }, [searchQuery, products, activeCategory]);

  const cartSubtotal = useMemo(
    () =>
      cart.reduce(
        (sum, item) => sum + Number(item.price || 0) * item.quantity,
        0
      ),
    [cart]
  );

  const tax = useMemo(
    () => cartSubtotal * (taxRate / 100),
    [cartSubtotal, taxRate]
  );

  const total = useMemo(
    () => cartSubtotal + tax - discountAmount,
    [cartSubtotal, tax, discountAmount]
  );

  const addToCart = (product) => {

    if (product.quantity <= 0) {
      toast.error(`${product.name} is out of stock!`);
      return;
    }

    setCart(prev => {

      const existing = prev.find(item => item.id === product.id);

      if (existing) {

        if (existing.quantity >= product.quantity) {
          toast.error(`No more stock for ${product.name}`);
          return prev;
        }

        return prev.map(item =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }

      return [
        ...prev,
        { ...product, quantity: 1, stock: product.quantity }
      ];

    });

  };

  const updateQuantity = (productId, delta) => {

    setCart(cart =>
      cart
        .map(item => {

          if (item.id !== productId) return item;

          const newQty = item.quantity + delta;

          if (newQty > item.stock) {
            toast.error(
              `Only ${item.stock} available for ${item.name}`
            );
            return item;
          }

          return newQty > 0
            ? { ...item, quantity: newQty }
            : null;

        })
        .filter(Boolean)
    );

  };

  const removeFromCart = (id) =>
    setCart(prev => prev.filter(item => item.id !== id));

  const handleCompleteSale = async () => {
  if (cart.length === 0) {
    toast.error("Cart is empty!");
    return;
  }

  const token = getToken();
  if (!token) {
    toast.error("No session. Please login.");
    navigate("/");
    return;
  }

  const saleData = {
    user_id: user?.id ?? null,
    payment_method: paymentMethod,
    subtotal: cartSubtotal.toFixed(2),
    tax_amount: tax.toFixed(2),
    discount_amount: discountAmount.toFixed(2),
    total_amount: total.toFixed(2),
    items: cart.map(item => ({
      product_id: item.id,
      quantity: item.quantity,
      price: item.price
    }))
  };

  toast.promise(
    axios.post(`${API_BASE_URL}/complete_sale.php`, saleData, {
      headers: { Authorization: `Bearer ${token}` }
    }),
    {
      loading: "Processing sale...",
      success: (res) => {
        if (res.data.status === "success") {
          setCart([]);
          setDiscountAmount(0);
          setTaxRate(5);
          return `Sale #${res.data.transaction_id || "completed"} successful!`;
        }
        throw new Error(res.data.message || "Sale failed");
      },
      error: (err) => {
        const backendMessage = err.response?.data?.message;
        const status = err.response?.data?.status;

        // Handle expired license
        if (status === "expired") {
          toast.error("Your license has expired. Please renew!");
          localStorage.removeItem("token");
          navigate("/renew-license");
          return; // stop further processing
        }

        // Handle token/session issues
        if (err.response?.status === 401 || err.response?.status === 403) {
          toast.error("Session expired. Please login again.");
          localStorage.removeItem("token");
          navigate("/");
          return;
        }

        // Default error
        return backendMessage || `Sale failed: ${err.message || "unknown error"}`;
      }
    }
  );
};

  return (
    <div className="pos-container">

      <SideNav activePage="POS" />

      <main className="pos-main-content">

        <div className="pos-products-section">

          <header className="products-header">
            <h1>Point of Sale</h1>
            <p>Welcome, {user ? user.name : "Pharmacist"}!</p>
          </header>

          <div className="search-bar-container">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search products..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <div className="category-filters">
            {productCategories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`category-btn ${
                  activeCategory === cat ? "active" : ""
                }`}
              >
                {cat}
              </button>
            ))}
          </div>

          <div className="product-grid">

            {loading ? (
              <p>Loading products...</p>
            ) : filteredProducts.length === 0 ? (
              <p>No products found.</p>
            ) : (
              filteredProducts.map(p => (
                <ProductCard
                  key={p.id}
                  product={p}
                  onAddToCart={addToCart}
                />
              ))
            )}

          </div>

        </div>

        <div className="pos-billing-section">

          <header className="order-header">
            <h2>Current Order</h2>
          </header>

          <div className="cart-items-container">

            {cart.length === 0 ? (

              <div className="cart-empty">
                <FaPills className="icon" />
                <p>Cart is empty</p>
              </div>

            ) : (

              cart.map(item => (

                <div key={item.id} className="cart-item">

                  <div className="cart-item-details">

                    <p>{item.name}</p>

                    <div className="quantity-controls">

                      <button onClick={() => updateQuantity(item.id, -1)}>
                        <FaMinus size={10} />
                      </button>

                      <span>{item.quantity}</span>

                      <button onClick={() => updateQuantity(item.id, 1)}>
                        <FaPlus size={10} />
                      </button>

                    </div>

                  </div>

                  <p className="cart-item-price">
                    {formatCurrency(item.price * item.quantity)}
                  </p>

                  <button
                    onClick={() => removeFromCart(item.id)}
                    className="remove-item-btn"
                  >
                    <FaTrash size={14} />
                  </button>

                </div>

              ))

            )}

          </div>

          {cart.length > 0 && (

            <div className="totals-section">

              <div className="totals-row">
                <span>Subtotal</span>
                <span>{formatCurrency(cartSubtotal)}</span>
              </div>

              <div className="totals-row">
                <span>Tax ({taxRate}%)</span>
                <span>{formatCurrency(tax)}</span>
              </div>

              <div className="totals-row">
                <span>Discount</span>
                <span>-{formatCurrency(discountAmount)}</span>
              </div>

              <div className="final-total">
                <span>Total</span>
                <span>{formatCurrency(total)}</span>
              </div>

              <div className="payment-options">

                <button
                  onClick={() => setPaymentMethod("Cash")}
                  className={`payment-btn ${
                    paymentMethod === "Cash" ? "active" : ""
                  }`}
                >
                  <FaCashRegister /> Cash
                </button>

                <button
                  onClick={() => setPaymentMethod("Card")}
                  className={`payment-btn ${
                    paymentMethod === "Card" ? "active" : ""
                  }`}
                >
                  <FaCreditCard /> Card
                </button>

              </div>

              <button
                onClick={handleCompleteSale}
                className="complete-sale-btn"
              >
                <FaReceipt /> Complete Sale
              </button>

            </div>

          )}

        </div>

      </main>

    </div>
  );
};

export default DashboardPage;